import React, { useEffect, useMemo, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { OPCIONES_GOOGLE_MAPS } from '../../mapas/opcionesGoogleMaps';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { esCritico } from './alertaVisual';

/**
 * El punto donde se genero la alerta.
 *
 * La coordenada sale de monitoreo_det, que ya la guarda: medido sobre 22.304
 * alertas de siete dias, 21.075 la tienen (94,5%). No hace falta tocar
 * dispositivo_posicion, que pesa 1,5 TB.
 *
 * Usa la misma libreria y la misma clave que el resto del proyecto
 * (MapaCalorGps, Operador, ReporteDispositivos). La clave esta repetida en
 * cinco archivos; centralizarla es una limpieza aparte.
 *
 * LAS CAPAS SE DIBUJAN A MANO, no con los componentes <Marker> y <Circle> de la
 * libreria. Con React 18 en StrictMode el arbol se monta, se desmonta y se
 * vuelve a montar; en ese ciclo la libreria destruye los objetos de Google y no
 * los vuelve a enganchar, asi que el mapa quedaba sin marcador y sin halo.
 * Reproducido: fuera de StrictMode se veian, dentro no. Creandolos en un efecto
 * con su propia limpieza, el remonte los vuelve a crear y siempre se ven.
 */


const VISTAS = [
    { id: 'roadmap', label: 'Mapa', icono: 'pi-map' },
    { id: 'hybrid', label: 'Satélite', icono: 'pi-globe' }
];

const ZOOM_INICIAL = 15;

/**
 * El marcador se dibuja a mano y no con el pin por defecto de Google.
 *
 * El pin de Google se confunde con los cientos de iconos de comercios que el
 * mapa ya trae encima: en el detalle no se distinguia cual de todos los
 * circulos era la alerta. Este va con borde blanco grueso y color segun
 * criticidad, la misma regla que usan las etiquetas de la grilla.
 */
const PIN = 'M 0,0 C -2,-19 -9,-21 -9,-28 A 9,9 0 1,1 9,-28 C 9,-21 2,-19 0,0 z';

const MapaAlerta = ({ lat, lng, puntoCercano, titulo, evento, alto = 260 }) => {

    const [vista, setVista] = useState('roadmap');
    const [zoom, setZoom] = useState(ZOOM_INICIAL);
    const [ampliado, setAmpliado] = useState(false);

    // Instancias en estado y no en ref: el efecto que dibuja las capas tiene que
    // volver a correr cuando el mapa se crea, y un ref no lo despierta.
    const [mapaChico, setMapaChico] = useState(null);
    const [mapaGrande, setMapaGrande] = useState(null);

    const { isLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);

    const centro = useMemo(() => ({ lat: Number(lat), lng: Number(lng) }), [lat, lng]);
    const grave = esCritico(evento);
    const color = grave ? '#c2381f' : '#e8a317';

    const sinCoordenada = lat == null || lng == null
        || Number(lat) === 0 || Number(lng) === 0
        || isNaN(Number(lat)) || isNaN(Number(lng));

    const activo = ampliado ? mapaGrande : mapaChico;

    // Las capas: halo de 150 m para dar la escala -- sin el, un punto solo no
    // dice si el camion estaba en un patio o en medio de la via -- un circulo
    // chico que hace de diana, y el pin encima de todo.
    useEffect(() => {
        if (!isLoaded || !window.google || sinCoordenada) return undefined;
        const g = window.google.maps;
        const destinos = [mapaChico, mapaGrande].filter(Boolean);
        const capas = [];
        destinos.forEach((mapa) => {
            capas.push(new g.Circle({
                map: mapa, center: centro, radius: 150, clickable: false,
                strokeColor: color, strokeOpacity: 0.7, strokeWeight: 2,
                fillColor: color, fillOpacity: 0.15
            }));
            capas.push(new g.Circle({
                map: mapa, center: centro, radius: 40, clickable: false,
                strokeColor: '#ffffff', strokeOpacity: 0.9, strokeWeight: 2,
                fillColor: color, fillOpacity: 0.35
            }));
            capas.push(new g.Marker({
                map: mapa, position: centro, title: titulo || 'Alerta', zIndex: 999,
                icon: {
                    path: PIN, fillColor: color, fillOpacity: 1,
                    strokeColor: '#ffffff', strokeWeight: 3, scale: 1.3,
                    anchor: new g.Point(0, 0)
                }
            }));
        });
        return () => capas.forEach((c) => c.setMap(null));
    }, [isLoaded, mapaChico, mapaGrande, centro, color, titulo, sinCoordenada]);

    const acercar = (delta) => {
        const z = Math.min(20, Math.max(3, zoom + delta));
        setZoom(z);
        if (activo) activo.setZoom(z);
    };

    const centrar = () => {
        setZoom(ZOOM_INICIAL);
        if (activo) {
            activo.setZoom(ZOOM_INICIAL);
            activo.panTo(centro);
        }
    };

    // Sin coordenada no se dibuja un mapa vacio: se dice por que no hay.
    if (sinCoordenada) {
        return (
            <div className="p-3 border-1 border-round surface-100 text-center"
                 style={{ borderColor: 'var(--surface-border)' }}>
                <i className="pi pi-map-marker text-color-secondary" style={{ fontSize: '1.4rem' }} />
                <div className="text-sm text-color-secondary mt-2">
                    Esta alerta no tiene coordenada registrada.
                </div>
                <div className="text-xs text-color-secondary mt-1">
                    Pasa en cerca del 5% de los casos, cuando el equipo no reportó posición
                    al momento del evento.
                </div>
            </div>
        );
    }

    const mapa = (altura, alCargar) => (
        <GoogleMap
            mapContainerStyle={{ width: '100%', height: altura }}
            center={centro}
            zoom={zoom}
            onLoad={alCargar}
            onUnmount={() => alCargar(null)}
            mapTypeId={vista}
            options={{
                // Los controles propios van arriba: los de Google se superponen
                // mal dentro de un dialogo chico.
                disableDefaultUI: true,
                gestureHandling: 'greedy',   // zoom con la rueda sin ctrl
                clickableIcons: false,
                keyboardShortcuts: false
            }} />
    );

    const barra = (
        <div className="flex align-items-center gap-1 p-2 surface-50 flex-wrap"
             style={{ borderBottom: '1px solid var(--surface-border)' }}>
            {VISTAS.map((v) => (
                <Button key={v.id} label={v.label} icon={`pi ${v.icono}`}
                        className={`p-button-sm ${vista === v.id ? '' : 'p-button-text'}`}
                        onClick={() => setVista(v.id)} />
            ))}
            <span className="ml-auto flex gap-1">
                <Button icon="pi pi-search-minus" className="p-button-sm p-button-text"
                        tooltip="Alejar" onClick={() => acercar(-1)} />
                <Button icon="pi pi-search-plus" className="p-button-sm p-button-text"
                        tooltip="Acercar" onClick={() => acercar(1)} />
                <Button icon="pi pi-compass" className="p-button-sm p-button-text"
                        tooltip="Centrar en la alerta" onClick={centrar} />
                <Button icon={ampliado ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
                        className="p-button-sm p-button-text"
                        tooltip={ampliado ? 'Reducir' : 'Ampliar'}
                        onClick={() => setAmpliado(!ampliado)} />
            </span>
        </div>
    );

    const pie = (
        <div className="flex justify-content-between gap-2 flex-wrap p-2 surface-50 text-xs"
             style={{ borderTop: '1px solid var(--surface-border)' }}>
            <span className="text-color-secondary">
                Coordenada{' '}
                <span style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}>
                    {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
                </span>
            </span>
            {puntoCercano && (
                <span className="text-color-secondary" style={{ maxWidth: '60%' }}>
                    {puntoCercano}
                </span>
            )}
        </div>
    );

    return (
        <>
            <div className="border-1 border-round overflow-hidden"
                 style={{ borderColor: 'var(--surface-border)' }}>
                {barra}
                {!isLoaded ? (
                    <div className="flex align-items-center justify-content-center surface-100"
                         style={{ height: alto }}>
                        <span className="text-color-secondary text-sm">Cargando el mapa…</span>
                    </div>
                ) : mapa(alto, setMapaChico)}
                {pie}
            </div>

            {/* Ampliado: en un dialogo casi a pantalla completa. En el detalle el
                mapa entra en 260 px y a esa altura no se distingue la calle. */}
            <Dialog header="Dónde ocurrió" visible={ampliado} modal
                    onHide={() => setAmpliado(false)}
                    style={{ width: '95vw', maxWidth: '1200px' }}
                    breakpoints={{ '768px': '100vw' }}
                    contentStyle={{ padding: 0 }}>
                {barra}
                {isLoaded && mapa('70vh', setMapaGrande)}
                {pie}
            </Dialog>
        </>
    );
};

export default MapaAlerta;
